import { Link, useParams, useNavigate } from "react-router-dom";
import { CITY_PAGES, CONDITIONS, CLINIC, FAQS } from "@/data/clinic";
import { ArrowRight, CheckCircle2, Phone, MessageCircle, GraduationCap, Award, Stethoscope, ShieldCheck } from "lucide-react";
import AppointmentForm from "@/components/AppointmentForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead, { physicianSchema, localBusinessSchema } from "@/components/SEOHead";

export default function CityPage({ slug: slugProp }) {
  const params = useParams();
  const nav = useNavigate();
  const slug = slugProp || params.slug;
  const page = CITY_PAGES.find((p) => p.slug === slug);
  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-poppins text-3xl text-slate-900">Page not found</h1>
        <button onClick={() => nav("/")} className="mt-6 bg-green-700 text-white rounded-full px-6 py-3 text-sm">Back to home</button>
      </div>
    );
  }

  const pageFaqs = FAQS.slice(0, 6);

  return (
    <div data-testid={`city-page-${page.slug}`}>
      <SEOHead
        title={page.metaTitle}
        description={page.metaDesc}
        path={`/${page.slug}`}
        image={page.image}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: page.title, url: `/${page.slug}` },
        ]}
        faqs={pageFaqs}
        schemas={[
          { id: "ld-physician", data: physicianSchema() },
          { id: "ld-localbiz", data: localBusinessSchema() },
        ]}
      />

      {/* Breadcrumb */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-green-700">Home</Link> <span className="mx-1.5">/</span>
        <span className="text-slate-700">{page.title}</span>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-50/70 via-white to-green-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> Senior Specialist • Kabir Nagar, Varanasi
            </div>
            <h1 className="font-poppins font-semibold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-4 leading-[1.05] tracking-tight">
              {page.h1}
            </h1>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{page.intro[0]}</p>
            {page.intro[1] && <p className="mt-4 text-slate-600 leading-relaxed">{page.intro[1]}</p>}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="city-book-btn" className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-3.5 font-medium text-sm shadow">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:+91${CLINIC.phone}`} className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-7 py-3 font-medium text-sm">
                <Phone className="w-4 h-4" /> Call {CLINIC.phoneDisplay}
              </a>
              <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-emerald-500 text-white rounded-full px-6 py-3 font-medium text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={CLINIC.doctorPhoto} alt={`${CLINIC.doctorName} — ${page.h1}`} className="w-full h-[440px] object-cover rounded-3xl border border-slate-100 shadow-xl" />
          </div>
        </div>
      </section>

      {/* Detail Sections (H2s) */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-12">
        {page.sections.map((s, i) => (
          <article key={i}>
            <h2 className="font-poppins font-semibold text-2xl sm:text-3xl text-slate-900">{s.h2}</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">{s.body}</p>
          </article>
        ))}
      </section>

      {/* Credentials strip */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: GraduationCap, label: "MBBS, MD (IMS BHU)" },
            { icon: Award, label: "DFID, CMC Vellore" },
            { icon: GraduationCap, label: "DMSc Endocrinology (UK)" },
            { icon: Stethoscope, label: "20+ Years Experience" },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-3">
              <c.icon className="w-5 h-5 text-green-400" />
              <span className="text-sm text-slate-100">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Appointment block */}
      <section className="bg-green-50/40 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-poppins font-semibold text-3xl text-slate-900">Schedule your consultation</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              The earliest you address diabetes or hormone problems, the simpler your treatment plan and the better your long-term outcomes. Book a consultation with {CLINIC.doctorName} today.
            </p>
            <ul className="mt-6 space-y-2 text-slate-700">
              <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" /><span>Same-day appointment confirmation</span></li>
              <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" /><span>WhatsApp follow-up for established patients</span></li>
              <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" /><span>Comprehensive diabetes + hormone + kidney evaluation</span></li>
            </ul>
          </div>
          <AppointmentForm />
        </div>
      </section>

      {/* Related Treatments */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-poppins font-semibold text-3xl text-slate-900">Related treatment pages</h2>
        <div className="mt-7 grid md:grid-cols-3 gap-5">
          {page.relatedSlugs.map((sl) => {
            const c = CONDITIONS.find((x) => x.slug === sl);
            if (!c) return null;
            return (
              <Link key={sl} to={`/${sl}`} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition flex gap-4 items-center">
                <img src={c.image} alt={c.title} className="w-16 h-16 object-cover rounded-xl" />
                <div>
                  <div className="font-poppins font-semibold text-base text-slate-900">{c.title}</div>
                  <div className="text-xs text-green-700 mt-1 inline-flex items-center gap-1">Read more <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-3xl text-slate-900 text-center">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {pageFaqs.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:text-green-700">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/faqs" className="text-sm text-green-700 inline-flex items-center gap-1">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
